import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { apiServer } from "../config";

// Types based on API documentation
export interface SearchParams {
  // Common parameters
  latitude?: string | number;
  longitude?: string | number;
  userlatitude?: string | number;
  userlongitude?: string | number;
  page?: number;
  limit?: number;
  search?: string;

  // Search type specific parameters
  search_type?:
    | "venue_name"
    | "brand"
    | "location"
    | "business_type"
    | "area_maps"
    | "nearby_venues"
    | "locations"
    | "brands"
    | "business_user";
  search_value?: string;

  // Business type search
  types?: string;

  // Accessibility filters
  accessibilities?: string;
  unixTimestamp?: number;

  // Additional parameters from existing API
  showVenueOptions?: boolean;
  addinfo?: boolean;
  placesToReplaceString?: string;
  searchThisAreaClicked?: boolean;
  zoom?: number;
  new_map?: boolean;
  tagging_mode?: boolean;
  progressive_update?: boolean;
  saved_place_id?: string;
  saved_place_user_id?: string;
  saved_place_sortby?: string;
  venue_id?: string;
}

export interface BusinessBadge {
  id: number;
  name: string;
  icon: string;
}

export interface OpeningHour {
  day: number;
  open_time: number;
  close_time: number;
}

export interface Business {
  id: string;
  google_business_id: string;
  name: string;
  business_type: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  distance: string;
  reviewsSubmitted: number;
  reviewsDetailsSubmitted: number;
  photo_thumb: string;
  alt_text: string;
  taggingCategories: string;
  tag_ids: string;
  businessBadges: BusinessBadge[];
  opening_hours: OpeningHour[];
}

export interface FacetItem {
  value: string;
  count: number;
}

export interface Facets {
  locations?: FacetItem[];
  brand?: FacetItem[];
}

export interface SearchResponse {
  status: string;
  message: string;
  data: {
    businesses: Business[];
    search_original_distance: string;
    search_distance_used: string;
    total_count: number;
  };
  facets: Facets;
  filter_accessibility: any[];
}

export interface SplashPageItem {
  id: number;
  name: string;
  image: string;
  heading1: string;
  line1: string;
  heading2: string | null;
  line2: string | null;
  heading3: string | null;
  line3: string | null;
  button1: string | null;
  button_link1: string;
  button2: string | null;
  button_link2: string | null;
  image_type: string;
  alignment_type: string;
  position: number;
  active: number;
  display_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface SplashPageResponse {
  status: string;
  message: string;
  data: SplashPageItem[];
}

// Hook options
interface UseSociabilitySearchOptions {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
}

// Search hook
export function useSociabilitySearch(
  params: SearchParams,
  options: UseSociabilitySearchOptions = {},
): UseQueryResult<SearchResponse, Error> {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ["sociability-search", params],
    queryFn: async (): Promise<SearchResponse> => {
      const parameters: Record<string, string> = {
        search: params.search || "",
        latitude: params.latitude?.toString() || "",
        longitude: params.longitude?.toString() || "",
        userlatitude: params.userlatitude?.toString() || "",
        userlongitude: params.userlongitude?.toString() || "",
        page: params.page?.toString() || "1",
        limit: params.limit?.toString() || "40",
      };

      // Add optional parameters
      if (params.search_type !== undefined) {
        parameters.search_type = params.search_type;
      }
      if (params.search_value !== undefined) {
        parameters.search_value = params.search_value;
      }
      if (params.types !== undefined) {
        parameters.types = params.types;
      }
      if (params.accessibilities !== undefined) {
        parameters.accessibilities = params.accessibilities;
        parameters.unixTimestamp = params.unixTimestamp?.toString() || "";
      }

      // Add additional parameters from existing API
      if (params.showVenueOptions !== undefined) {
        parameters.showVenueOptions = params.showVenueOptions.toString();
      }
      if (params.addinfo !== undefined) {
        parameters.addinfo = params.addinfo.toString();
      }
      if (params.placesToReplaceString !== undefined) {
        parameters.placesToReplaceString = params.placesToReplaceString;
      }
      if (params.searchThisAreaClicked !== undefined) {
        parameters.searchThisAreaClicked =
          params.searchThisAreaClicked.toString();
      }
      if (params.zoom !== undefined) {
        parameters.zoom = params.zoom.toString();
      }
      if (params.new_map !== undefined) {
        parameters.new_map = params.new_map.toString();
      }
      if (params.tagging_mode !== undefined) {
        parameters.tagging_mode = params.tagging_mode.toString();
      }
      if (params.progressive_update !== undefined) {
        parameters.progressive_update = params.progressive_update.toString();
      }
      if (params.saved_place_id !== undefined) {
        parameters.saved_place_id = params.saved_place_id;
      }
      if (params.saved_place_user_id !== undefined) {
        parameters.saved_place_user_id = params.saved_place_user_id;
      }
      if (params.saved_place_sortby !== undefined) {
        parameters.saved_place_sortby = params.saved_place_sortby;
      }
      if (params.venue_id !== undefined) {
        parameters.venue_id = params.venue_id;
      }

      const queryString = new URLSearchParams(parameters).toString();
      const url = `${apiServer}/business/search?${queryString}`;

      console.log("url", url);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Search failed: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      return data;
    },
    enabled: enabled,
  });
}

export function useBrandSearch(
  brandName: string,
  options: UseSociabilitySearchOptions = {},
) {
  return useSociabilitySearch(
    {
      search_type: "brand" as const,
      search_value: brandName,
    },
    options,
  );
}

export function useLocationSearch(
  latitude: string | number,
  longitude: string | number,
  options: UseSociabilitySearchOptions = {},
) {
  return useSociabilitySearch(
    {
      search_type: "location" as const,
      latitude,
      longitude,
    },
    options,
  );
}

export function useBusinessTypeSearch(
  businessType: string,
  options: UseSociabilitySearchOptions = {},
) {
  return useSociabilitySearch(
    {
      search_type: "business_type" as const,
      search_value: businessType,
    },
    options,
  );
}

export function useAreaMapsSearch(
  latitude: string | number,
  longitude: string | number,
  options: UseSociabilitySearchOptions = {},
) {
  return useSociabilitySearch(
    {
      search_type: "locations" as const,
      search_value: "",
      latitude,
      longitude,
    },
    options,
  );
}

export function useNearbyVenuesSearch(
  parentBusinessId: string,
  options: UseSociabilitySearchOptions = {},
) {
  return useSociabilitySearch(
    {
      search_type: "nearby_venues" as const,
      search_value: parentBusinessId,
    },
    options,
  );
}

export function useLocationsList(options: UseSociabilitySearchOptions = {}) {
  return useSociabilitySearch(
    {
      search_type: "locations" as const,
      search_value: "",
    },
    options,
  );
}

export function useBrandsList(options: UseSociabilitySearchOptions = {}) {
  return useSociabilitySearch(
    {
      search_type: "brands" as const,
      search_value: "",
    },
    options,
  );
}

export function useMyVenuesFull(
  userId: string,
  options: UseSociabilitySearchOptions = {},
) {
  return useSociabilitySearch(
    {
      search_type: "business_user" as const,
      search_value: userId,
      latitude: 0,
      longitude: 0,
      userlatitude: 0,
      userlongitude: 0,
      page: 1,
      limit: 10,
      showVenueOptions: true,
      placesToReplaceString: "",
      searchThisAreaClicked: false,
      zoom: 14,
      new_map: true,
      progressive_update: true,
      saved_place_id: "",
      saved_place_user_id: "",
      saved_place_sortby: "",
      venue_id: "",
    },
    options,
  );
}

// Splash page hook
export function useSplashPage(
  options: UseSociabilitySearchOptions = {},
): UseQueryResult<SplashPageResponse, Error> {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ["splash-page"],
    queryFn: async (): Promise<SplashPageResponse> => {
      const response = await fetch(`${apiServer}/splash-page/load`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Splash page failed: ${response.status} ${response.statusText}`,
        );
      }

      return response.json();
    },
    enabled: enabled,
  });
}
